import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { Button } from '@innovyze/stylovyze';
import { Warning } from '@mui/icons-material';
import { useQueries } from '@tanstack/react-query';
import type { RehabAction } from '@Types/rehab.types';
import { isCostTableAction } from '@Types/rehab.types';
import type { CostTableDetail } from '@Types/cost.types';
import { costTableDetailQuery } from '../../queries/costTableQueries';

interface Props {
	actions: RehabAction[];
	onUpdateAndRun?: (
		updates: Array<{ actionId: string; costTableDataId: string }>,
	) => void;
	isDisabled?: boolean;
}

export const CostTableStalenessWarning = ({
	actions,
	onUpdateAndRun,
	isDisabled,
}: Props) => {
	const { t } = useTranslation();

	const costTableActions = actions.filter(isCostTableAction);

	// Deduplicate by costTableId so we only fetch each cost table once
	const uniqueCostTableIds = Array.from(
		new Set(costTableActions.map(a => a.costTableId)),
	);

	const queries = useQueries({
		queries: uniqueCostTableIds.map(id => costTableDetailQuery(id)),
	});

	// Build a map of costTableId → latest data id
	const detailMap = new Map<string, CostTableDetail>();
	queries.forEach((q, i) => {
		if (q.data) {
			detailMap.set(uniqueCostTableIds[i], q.data);
		}
	});

	// Find all stale cost tables and collect updates
	const staleNames: string[] = [];
	const updates: Array<{ actionId: string; costTableDataId: string }> = [];
	const seen = new Set<string>();
	for (const action of costTableActions) {
		const detail = detailMap.get(action.costTableId);
		if (
			detail &&
			action.costTableDataId !== detail.latest_cost_table_data_id
		) {
			updates.push({
				actionId: action._id,
				costTableDataId: detail.latest_cost_table_data_id,
			});
			if (!seen.has(action.costTableId)) {
				staleNames.push(detail.name);
			}
		}
		seen.add(action.costTableId);
	}

	if (staleNames.length === 0) return null;

	const message =
		staleNames.length === 1
			? t(
					'Cost table {{name}} has been updated since last run. Re-run to see updated costs.',
					{ name: staleNames[0] },
				)
			: t(
					'Cost tables {{names}} have been updated since last run. Re-run to see updated costs.',
					{ names: staleNames.join(', ') },
				);

	return (
		<Box
			data-cy="staleness-warning-banner"
			sx={{
				display: 'flex',
				width: '100%',
				overflow: 'hidden',
				border: '1px solid #EDA641',
				mb: 1,
			}}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: '#EDA641',
					padding: '8px',
				}}>
				<Warning sx={{ color: '#FFFEFE', fontSize: '20px' }} />
			</Box>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					backgroundColor: '#FBEDD4',
					padding: '8px 12px',
					flex: 1,
					gap: 2,
				}}>
				<Typography
					sx={{
						color: '#746E67',
						fontSize: '0.875rem',
						fontWeight: 'bold',
						flex: 1,
					}}>
					{message}
				</Typography>
				{onUpdateAndRun && (
					<Button
						size="small"
						variant="contained"
						disabled={isDisabled}
						sx={{
							backgroundColor: '#EDA641',
							'&:hover': { backgroundColor: '#d4922e' },
							'&.Mui-disabled': {
								backgroundColor: '#EDA641',
								opacity: 0.6,
								color: 'white',
							},
						}}
						onClick={() => onUpdateAndRun(updates)}>
						{t('Update & Run')}
					</Button>
				)}
			</Box>
		</Box>
	);
};
